import { useEffect, useState } from 'react';
import { UserPlus, Users, Search, RefreshCw, Loader2 } from 'lucide-react';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import api from '@/lib/api';
import { useUIStore } from '@/stores/ui-store';

interface Enrollment {
  id: string;
  student_name: string;
  student_email: string;
  student_id: string;
  course_id: string;
  course_name: string;
  status: 'active' | 'completed' | 'expired' | 'suspended' | 'cancelled';
  source: 'purchase' | 'manual' | 'gift' | 'subscription' | 'coupon';
  completion_percentage: number;
  enrolled_at: string;
  expires_at: string | null;
  completed_at: string | null;
}

interface EnrollmentsResponse {
  enrollments: Enrollment[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

interface CourseOption {
  id: string;
  name: string;
}

const statusVariants: Record<Enrollment['status'], 'green' | 'blue' | 'yellow' | 'red' | 'gray'> = {
  active: 'green',
  completed: 'blue',
  expired: 'yellow',
  suspended: 'red',
  cancelled: 'gray',
};

const sourceLabels: Record<Enrollment['source'], string> = {
  purchase: 'Purchase',
  manual: 'Manual',
  gift: 'Gift',
  subscription: 'Subscription',
  coupon: 'Coupon',
};

const Enrollments = () => {
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [courses, setCourses] = useState<CourseOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, total_pages: 0 });

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [courseFilter, setCourseFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');

  // Manual enroll modal
  const [enrollModalOpen, setEnrollModalOpen] = useState(false);
  const [enrollForm, setEnrollForm] = useState({ email: '', course_id: '' });
  const [isEnrolling, setIsEnrolling] = useState(false);

  // Status update
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = useState<Enrollment | null>(null);
  const [newStatus, setNewStatus] = useState('');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  useEffect(() => {
    fetchCourses();
  }, []);

  useEffect(() => {
    fetchEnrollments();
  }, [pagination.page, searchQuery, courseFilter, statusFilter, sourceFilter]);

  async function fetchCourses() {
    try {
      const response = await api.get<{ courses: CourseOption[] }>('/admin/courses?limit=100');
      setCourses(response.courses);
    } catch {
      // Non-critical
    }
  }

  async function fetchEnrollments() {
    try {
      setIsLoading(true);
      setError(null);

      const params = new URLSearchParams();
      params.set('page', pagination.page.toString());
      params.set('limit', pagination.limit.toString());
      if (searchQuery) params.set('q', searchQuery);
      if (courseFilter) params.set('course_id', courseFilter);
      if (statusFilter) params.set('status', statusFilter);
      if (sourceFilter) params.set('source', sourceFilter);

      const response = await api.get<EnrollmentsResponse>(`/admin/enrollments?${params.toString()}`);
      setEnrollments(response.enrollments);
      setPagination(response.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load enrollments');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleManualEnroll() {
    if (!enrollForm.email || !enrollForm.course_id) {
      useUIStore.getState().addToast('Email and course are required', 'error');
      return;
    }

    try {
      setIsEnrolling(true);
      await api.post('/admin/enrollments', {
        email: enrollForm.email,
        course_id: enrollForm.course_id,
        source: 'manual',
      });
      useUIStore.getState().addToast('Student enrolled successfully', 'success');
      setEnrollModalOpen(false);
      setEnrollForm({ email: '', course_id: '' });
      fetchEnrollments();
    } catch (err: any) {
      useUIStore.getState().addToast(err.message || 'Failed to enroll student', 'error');
    } finally {
      setIsEnrolling(false);
    }
  }

  async function handleStatusUpdate() {
    if (!selectedEnrollment || !newStatus) return;

    try {
      setIsUpdatingStatus(true);
      await api.put(`/admin/enrollments/${selectedEnrollment.id}/status`, { status: newStatus });
      useUIStore.getState().addToast('Enrollment status updated', 'success');
      setStatusModalOpen(false);
      setSelectedEnrollment(null);
      setNewStatus('');
      fetchEnrollments();
    } catch (err: any) {
      useUIStore.getState().addToast(err.message || 'Failed to update status', 'error');
    } finally {
      setIsUpdatingStatus(false);
    }
  }

  function openStatusModal(enrollment: Enrollment) {
    setSelectedEnrollment(enrollment);
    setNewStatus(enrollment.status);
    setStatusModalOpen(true);
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Enrollments</h1>
          <p className="text-gray-600 mt-1">Manage student course enrollments</p>
        </div>
        <Button variant="primary" size="md" onClick={() => setEnrollModalOpen(true)}>
          <UserPlus className="w-5 h-5 mr-2" />
          Manual Enroll
        </Button>
      </div>

      {/* Filters */}
      <Card padding="md">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPagination((p) => ({ ...p, page: 1 }));
              }}
              placeholder="Search by name or email..."
              className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <Select
            value={courseFilter}
            onChange={(e) => {
              setCourseFilter(e.target.value);
              setPagination((p) => ({ ...p, page: 1 }));
            }}
            placeholder="All Courses"
            options={[
              { label: 'All Courses', value: '' },
              ...courses.map((c) => ({ label: c.name, value: c.id })),
            ]}
          />
          <Select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPagination((p) => ({ ...p, page: 1 }));
            }}
            placeholder="All Statuses"
            options={[
              { label: 'All Statuses', value: '' },
              { label: 'Active', value: 'active' },
              { label: 'Completed', value: 'completed' },
              { label: 'Expired', value: 'expired' },
              { label: 'Suspended', value: 'suspended' },
              { label: 'Cancelled', value: 'cancelled' },
            ]}
          />
          <Select
            value={sourceFilter}
            onChange={(e) => {
              setSourceFilter(e.target.value);
              setPagination((p) => ({ ...p, page: 1 }));
            }}
            placeholder="All Sources"
            options={[
              { label: 'All Sources', value: '' },
              { label: 'Purchase', value: 'purchase' },
              { label: 'Manual', value: 'manual' },
              { label: 'Gift', value: 'gift' },
              { label: 'Subscription', value: 'subscription' },
              { label: 'Coupon', value: 'coupon' },
            ]}
          />
        </div>
      </Card>

      {/* Table */}
      <Card padding="none">
        {isLoading ? (
          <div className="flex justify-center items-center py-16">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <p className="text-red-600 mb-4">{error}</p>
            <Button variant="secondary" size="sm" onClick={fetchEnrollments}>
              <RefreshCw className="w-4 h-4 mr-1" />
              Retry
            </Button>
          </div>
        ) : enrollments.length === 0 ? (
          <div className="text-center py-16">
            <Users className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p className="text-gray-600">No enrollments found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left px-4 py-3 font-medium text-gray-700">Student</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-700">Course</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-700">Status</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-700">Source</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-700">Progress</th>
                    <th className="text-left px-4 py-3 font-medium text-gray-700">Enrolled</th>
                    <th className="text-right px-4 py-3 font-medium text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {enrollments.map((enrollment) => (
                    <tr key={enrollment.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{enrollment.student_name}</div>
                        <div className="text-xs text-gray-500">{enrollment.student_email}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{enrollment.course_name}</td>
                      <td className="px-4 py-3">
                        <Badge variant={statusVariants[enrollment.status]}>
                          {enrollment.status.charAt(0).toUpperCase() + enrollment.status.slice(1)}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-gray-600">{sourceLabels[enrollment.source]}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-20 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-500 h-2 rounded-full"
                              style={{ width: `${enrollment.completion_percentage}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-600 w-10 text-right">
                            {enrollment.completion_percentage}%
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {new Date(enrollment.enrolled_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openStatusModal(enrollment)}
                        >
                          Update Status
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                <span className="text-sm text-gray-600">
                  Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={pagination.page <= 1}
                    onClick={() => setPagination((p) => ({ ...p, page: p.page - 1 }))}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={pagination.page >= pagination.total_pages}
                    onClick={() => setPagination((p) => ({ ...p, page: p.page + 1 }))}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Manual Enroll Modal */}
      <Modal isOpen={enrollModalOpen} onClose={() => setEnrollModalOpen(false)} title="Manual Enrollment">
        <div className="space-y-4">
          <Input
            label="Student Email"
            type="email"
            value={enrollForm.email}
            onChange={(e) => setEnrollForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="student@example.com"
          />
          <Select
            label="Course"
            value={enrollForm.course_id}
            onChange={(e) => setEnrollForm((f) => ({ ...f, course_id: e.target.value }))}
            placeholder="Select a course"
            options={[
              { label: 'Select a course', value: '' },
              ...courses.map((c) => ({ label: c.name, value: c.id })),
            ]}
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" size="sm" onClick={() => setEnrollModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleManualEnroll} disabled={isEnrolling}>
              {isEnrolling ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              {isEnrolling ? 'Enrolling...' : 'Enroll Student'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Update Status Modal */}
      <Modal isOpen={statusModalOpen} onClose={() => setStatusModalOpen(false)} title="Update Enrollment Status">
        <div className="space-y-4">
          {selectedEnrollment && (
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm font-medium text-gray-900">{selectedEnrollment.student_name}</p>
              <p className="text-xs text-gray-500">{selectedEnrollment.course_name}</p>
            </div>
          )}
          <Select
            label="New Status"
            value={newStatus}
            onChange={(e) => setNewStatus(e.target.value)}
            options={[
              { label: 'Active', value: 'active' },
              { label: 'Completed', value: 'completed' },
              { label: 'Expired', value: 'expired' },
              { label: 'Suspended', value: 'suspended' },
              { label: 'Cancelled', value: 'cancelled' },
            ]}
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" size="sm" onClick={() => setStatusModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleStatusUpdate} disabled={isUpdatingStatus}>
              {isUpdatingStatus ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              {isUpdatingStatus ? 'Updating...' : 'Update Status'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Enrollments;
